// Screen 11, the practice dashboard (CR-002 CR2-050, CR2-053; J-086), at /practice?year&aco&tin&npi.
// The year and ACO come from the shell's selectors; "All" ACOs lists every ACO's practices (CR-005 item 6). The
// configured texts and columns are read here, where features are composed (C6).
// J-209 (the owner's inbox file 06, PR D): "All" opens the default ACO's first practice (item 3); a
// failed or refused read says so (item 7); an ACO code not in scope is "not found" (item 11).
// J-232 (the owner's inbox file 33, items 2 and 4): "View by: Practice | Performance group", kept in the
// URL as on Performance (`?view=groups&group=<groupId>`), for a user with ACO-wide scope; for anyone else
// the guard drops both before any read is sent.
// J-238 (group access): a group-scoped user sees only the group view, of one of its granted groups; the
// guard opens it on the first granted group when the URL names none or another.
// J-277 (QB3 items 8, 8b, 8c): "View by: Performance group | Practice", the group view first and the
// default; the URL names the practice view (`view=practices`), `view=groups` still opens the group view,
// and a pre-J-277 link naming a practice (`?tin=` with no view) is replaced with its explicit practice
// view (`lib/screen-view.ts`). The practice view keeps the Performance group filter (`group`), the group
// view a Practice field (`tin`).
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { uiDocumentSchema } from "@vbltc/schema";

import { metadataQueryOptions } from "@/features/aco-shell/api/metadata";
import { uiConfigQueryOptions } from "@/features/aco-shell/api/ui-config";
import { AcoNotFound } from "@/features/aco-shell/components/aco-not-found";
import { ShellStatusMessage } from "@/features/aco-shell/components/shell-status-message";
import { ALL_ACOS, defaultAcoCode, resolveYear } from "@/features/aco-shell/default-aco";
import { shellStatus } from "@/features/aco-shell/shell-status";
import { PracticeGroupScreen } from "@/features/aco-practice/components/practice-group-screen";
import { PracticeScreen } from "@/features/aco-practice/components/practice-screen";
import { ViewSwitch } from "@/features/aco-groups/components/view-switch";
import { accessGroupRefs } from "@/api/group-practices";
import { meAccessQueryOptions, readMyAccess } from "@/features/auth/api/me-access";
import { hasGroupScope } from "@/features/auth/group-scope";
import { usePermission } from "@/features/auth/use-permission";
import { grantedGroupSearch, isGroupScoped } from "@/lib/group-access";
import { dropGroupScope, practiceSearchSchema } from "@/lib/route-search";
import { explicitView, requestedView, viewKey } from "@/lib/screen-view";

export const Route = createFileRoute("/_authenticated/practice/")({
  validateSearch: practiceSearchSchema,
  // J-232 (item 4): the search check cannot see the role, so the guard drops the group view and its
  // group for a role without ACO-wide scope, replacing the URL before the screen draws.
  beforeLoad: async ({ context, search }) => {
    const access = await readMyAccess(context.queryClient);
    if (isGroupScoped(access)) {
      const granted = grantedGroupSearch(access.groups, search);
      // The practice view reads the whole ACO's practices, so a group user always has the group view.
      if (granted !== undefined || requestedView(search) === "practices")
        throw redirect({
          to: "/practice",
          search: {
            ...search,
            ...(granted === undefined ? {} : { ...granted, npi: undefined }),
            view: undefined,
            tin: undefined,
          },
          replace: true,
        });
      return;
    }
    const acoWide = hasGroupScope(context.session.user.role, access?.scopeKind);
    // J-278 (QB3 item 11a): a practice user who sees performance groups has the group view too.
    const practiceGroups = access?.scopeKind === "practice" && access.groups.length > 0;
    // J-278 with J-251: a group the practice user does not see is dropped before any read, as the URL
    // of a group user's foreign group is.
    const foreign =
      practiceGroups &&
      search.group !== undefined &&
      !access.groups.some((g) => g.groupId === search.group);
    const kept = foreign
      ? { ...search, group: undefined, groupAco: undefined }
      : dropGroupScope(search, acoWide || practiceGroups);
    // J-277: an old practice link becomes its explicit practice view, which a header change keeps.
    const explicit = acoWide ? explicitView(search) : undefined;
    // J-301 (QB4 item 3a): a practice user's "All" is every ACO of its own practices, so a URL naming
    // none stays as it is (J-251 gave it its first ACO).
    if (kept !== undefined || explicit !== undefined)
      throw redirect({ to: "/practice", search: kept ?? explicit ?? search, replace: true });
  },
  // The shell draws the permission state for a role or account the practices' read refuses (J-209).
  staticData: {
    screenName: "Quality Summary",
    requires: { acoPractice: ["read"] },
    ownScope: true,
  },
  component: PracticePage,
});

function PracticePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canReport = usePermission({ acoGaps: ["read"] });
  const access = useQuery(meAccessQueryOptions());
  const groupScoped = isGroupScoped(access.data);
  const canListGroups = usePermission({ acoQuality: ["read"] });
  // J-278 (QB3 item 11a): a practice user's groups, from /me/access, which the group view lists (it
  // cannot read /performance-groups); the practice view offers it no group filter.
  const accessGroups =
    access.data?.scopeKind === "practice" && access.data.groups.length > 0
      ? access.data.groups
      : undefined;
  const canSeeGroups = canListGroups || groupScoped || accessGroups !== undefined;
  const metadata = useQuery(metadataQueryOptions());
  const ui = useQuery(uiConfigQueryOptions());
  const document = uiDocumentSchema.safeParse(ui.data?.document);
  const status = shellStatus(metadata, { data: ui.data, error: ui.error, valid: document.success });
  if (status !== "ready" || metadata.data === undefined || !document.success)
    return (
      <ShellStatusMessage
        status={status === "ready" ? "loading" : status}
        onRetry={() => void Promise.all([metadata.refetch(), ui.refetch()])}
      />
    );
  const acoCode = search.aco === undefined || search.aco === ALL_ACOS ? undefined : search.aco;
  if (acoCode !== undefined && !metadata.data.acos.some((a) => a.acoCode === acoCode))
    return (
      <AcoNotFound
        acoCode={acoCode}
        fallback={defaultAcoCode(metadata.data.acos)}
        renderLink={(code) => (
          <Link
            to="/practice"
            search={{ year: search.year, aco: code ?? ALL_ACOS }}
            className="underline"
          >
            {code === undefined ? "Show every ACO's TIN Names" : `Open ${code}'s TIN Names`}
          </Link>
        )}
      />
    );
  const year = resolveYear(metadata.data, search.year, search.aco);
  if (year === undefined)
    return <p className="text-muted-foreground text-sm">No performance year is configured.</p>;
  const { text, screens } = document.data;
  const byGroup = groupScoped || (canSeeGroups && requestedView(search) === "groups");
  const practices = (
    <PracticeScreen
      year={year}
      acoCode={acoCode}
      tin={search.tin}
      npi={search.npi}
      defaultAco={acoCode ?? defaultAcoCode(metadata.data.acos, year)}
      text={{
        performanceLabel: text.performance_label,
        inverseLabel: text.inverse_label,
        lowerIsBetter: text.lower_is_better,
        dataLoaded: text.data_loaded,
      }}
      columns={screens.practice_dashboard.columns}
      onTinChange={(tin) => void navigate({ search: { ...search, tin, npi: undefined } })}
      onNpiChange={(npi) => void navigate({ search: { ...search, npi } })}
      {...(canListGroups || groupScoped
        ? {
            groupFilter: {
              group: search.group,
              // Item 8b: another group opens its first practice, with every clinician.
              onGroupChange: (group: number | undefined) =>
                void navigate({
                  search: { ...search, view: "practices", group, tin: undefined, npi: undefined },
                }),
            },
          }
        : {})}
      {...(canReport
        ? {
            onOpenReport: (tin: string, measureNumber: string | undefined) =>
              void navigate({
                to: "/practice/gaps",
                search: {
                  year: search.year,
                  aco: search.aco,
                  tin,
                  npi: search.npi,
                  tab: measureNumber ?? "consolidated",
                },
              }),
          }
        : {})}
    />
  );
  if (!canSeeGroups) return practices;
  return (
    <div className="grid gap-4">
      {groupScoped ? null : (
        <ViewSwitch
          view={byGroup ? "groups" : "practices"}
          onViewChange={(view) =>
            void navigate({
              // The group carries over between the views (the practice view's filter, item 8b); the
              // practice and the clinician differ between them, so both are cleared.
              search: { ...search, view: viewKey(view), tin: undefined, npi: undefined },
            })
          }
        />
      )}
      {!byGroup ? (
        practices
      ) : (
        <PracticeGroupScreen
          year={year}
          headerAco={acoCode}
          group={search.group}
          tin={search.tin}
          npi={search.npi}
          text={{
            performanceLabel: text.performance_label,
            inverseLabel: text.inverse_label,
            lowerIsBetter: text.lower_is_better,
            dataLoaded: text.data_loaded,
          }}
          columns={screens.practice_dashboard.columns}
          // Item 8c: another group opens on "All practices", with every clinician; J-268: and on Both.
          onGroupChange={(group) =>
            void navigate({
              search: {
                ...search,
                view: undefined,
                group,
                tin: undefined,
                npi: undefined,
                groupAco: undefined,
              },
            })
          }
          onTinChange={(tin) =>
            void navigate({
              search: { ...search, view: viewKey("groups", tin), tin, npi: undefined },
            })
          }
          onNpiChange={(npi) => void navigate({ search: { ...search, npi } })}
          // J-268: the ACO filter of a group whose practices span ACOs; omitted is "Both". Another ACO
          // opens on "All practices" of the group there.
          figuresAco={search.groupAco}
          onFiguresAcoChange={(code) =>
            void navigate({
              search: {
                ...search,
                view: undefined,
                groupAco: code,
                tin: undefined,
                npi: undefined,
              },
            })
          }
          canReport={canReport}
          {...(groupScoped
            ? { grantedGroupIds: (access.data?.groups ?? []).map((g) => g.groupId) }
            : {})}
          {...(accessGroups === undefined
            ? {}
            : { accessGroups: accessGroupRefs(accessGroups, year) })}
        />
      )}
    </div>
  );
}
